import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Check, ClipboardList, Copy, Download, FolderArchive, Hourglass, Image as ImageIcon, Smartphone, Type } from "lucide-react";
import { toast } from "sonner";
import { KIT, KIT_ORDER, LAUNCH_DATE, teasersFor } from "@/data/kit";
import { getNetwork } from "@/data/networks";
import { Seo } from "@/components/hv/Seo";
import { FadeIn } from "@/components/hv/Motion";
import { PasswordGate, useUnlocked } from "@/components/hv/PasswordGate";
import { downloadKitZip } from "@/lib/kitZip";
import { ReplyBank } from "@/components/hv/ReplyBank";

const fmtDate = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
const daysUntil = (iso) => Math.ceil((new Date(`${iso}T00:00:00Z`) - Date.now()) / 86400000);

const CopyButton = ({ text, label = "Copy", testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setDone(true); toast.success("Copied to clipboard");
    setTimeout(() => setDone(false), 1800);
  };
  return (
    <button type="button" onClick={copy} data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full h-9 px-3.5 text-xs font-medium transition-colors duration-300 ${done ? "bg-[#1F1A17] text-[#FBF8F4]" : "border border-[#E8DDD3] bg-white text-[#1F1A17] hover:border-[#1F1A17]"}`}>
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {done ? "Copied" : label}
    </button>
  );
};

const Field = ({ label, value, i }) => (
  <div className="rounded-2xl border border-[#E8DDD3] bg-white p-5" data-testid={`kit-field-${i}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{label}</div>
      <CopyButton text={value} testId={`kit-field-copy-${i}`} />
    </div>
    <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-[#1F1A17]">{value}</pre>
    <div className="mt-2 font-mono text-[10px] text-[#685F58]">{value.length} chars</div>
  </div>
);

const Asset = ({ a, i }) => (
  <figure className="rounded-2xl border border-[#E8DDD3] bg-white overflow-hidden" data-testid={`kit-asset-${i}`}>
    <div className="bg-[#F3ECE5] p-4 grid place-items-center">
      <img src={`/kit/${a.file}`} alt={a.label} className="max-h-48 w-auto rounded-lg shadow-md" loading="lazy" />
    </div>
    <figcaption className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-[#1F1A17]">{a.label}</div>
          <div className="mt-1 text-xs text-[#685F58]">{a.dims}</div>
          <div className="mt-1 text-xs text-[#685F58]">{a.use}</div>
        </div>
        <a href={`/kit/${a.file}`} download data-testid={`kit-asset-download-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-9 px-3.5 text-xs font-medium text-[#FBF8F4] transition-colors duration-300 shrink-0">
          <Download className="h-3.5 w-3.5" /> Download
        </a>
      </div>
    </figcaption>
  </figure>
);

const Post = ({ p, i, accent }) => {
  const full = p.tags.length ? `${p.caption}\n\n${p.tags.join(" ")}` : p.caption;
  return (
    <article className="grid md:grid-cols-[220px_1fr] gap-5 rounded-2xl border border-[#E8DDD3] bg-white p-5" data-testid={`kit-post-${i}`}>
      <div>
        <div className="flex gap-3">
          <img src={`/kit/${p.image}`} alt="" className="min-w-0 flex-1 rounded-lg shadow-md object-cover self-start" loading="lazy" />
          {p.story && <img src={`/kit/${p.story}`} alt="" className="w-[72px] rounded-lg shadow-md" loading="lazy" data-testid={`kit-post-story-img-${i}`} />}
        </div>
        <div className="mt-3 flex flex-col gap-1.5">
          <a href={`/kit/${p.image}`} download data-testid={`kit-post-download-${i}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1F1A17] hover:text-[#B4532A]"><Download className="h-3.5 w-3.5" /> Download post image</a>
          {p.story && <a href={`/kit/${p.story}`} download data-testid={`kit-post-story-download-${i}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1F1A17] hover:text-[#B4532A]"><Smartphone className="h-3.5 w-3.5" /> Download Story / Reel cover (1080×1920)</a>}
        </div>
      </div>
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="rounded-full px-3 py-1 font-mono text-[11px] text-white" style={{ background: accent }}>{fmtDate(p.date)}</span>
            <h3 className="font-serif text-xl tracking-tight text-[#1F1A17]">{p.title}</h3>
          </div>
          <CopyButton text={full} label="Copy caption" testId={`kit-post-copy-${i}`} />
        </div>
        {p.note && <p className="mt-3 rounded-xl bg-[#FDEEE8] px-4 py-2.5 text-xs text-[#9C421E]" data-testid={`kit-post-note-${i}`}>How to: {p.note}</p>}
        <pre className="mt-4 whitespace-pre-wrap font-sans text-sm leading-relaxed text-[#1F1A17]">{p.caption}</pre>
        {p.tags.length > 0 && <p className="mt-3 text-xs text-[#B4532A]">{p.tags.join(" ")}</p>}
      </div>
    </article>
  );
};

const Section = ({ Icon, title, sub, children, testId }) => (
  <section className="mt-12" data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className="h-9 w-9 rounded-xl grid place-items-center bg-[#1F1A17] text-[#FBF8F4]"><Icon className="h-4 w-4" /></span>
      <div>
        <h2 className="font-serif text-2xl tracking-tight text-[#1F1A17]">{title}</h2>
        {sub && <p className="text-xs text-[#685F58]">{sub}</p>}
      </div>
    </div>
    <div className="mt-6">{children}</div>
  </section>
);

const Kit = ({ onLock }) => {
  const [tab, setTab] = useState("facebook");
  const k = KIT[tab];
  const teasers = teasersFor(tab);
  const n = getNetwork(tab);
  const days = daysUntil(LAUNCH_DATE);
  const Icon = n.Icon;
  const [zip, setZip] = useState({ busy: false, done: 0, total: 0 });

  const downloadZip = async () => {
    setZip({ busy: true, done: 0, total: 0 });
    try {
      await downloadKitZip(tab, n.name, (done, total) => setZip({ busy: true, done, total }));
      toast.success(`${n.name} kit downloaded`);
    } catch (e) {
      toast.error("Could not build the zip — please try again.");
    } finally {
      setZip({ busy: false, done: 0, total: 0 });
    }
  };

  return (
    <FadeIn className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div data-testid="kit-page-content">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#B4532A]">Hearthside · launch kit</div>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight text-[#1F1A17]">Copy, paste, upload. <em className="text-[#B4532A]">Pages done.</em></h1>
            <p className="mt-3 max-w-2xl text-sm text-[#685F58]">
              Profile copy at each platform's limits, correctly sized art, and a dated launch calendar. Every link already carries <code className="font-mono">utm_content</code>, so your stats page will show which post sent each signup.
            </p>
          </div>
          <div className="flex items-center gap-3">
          <Link to="/runsheet" data-testid="kit-runsheet-link" className="inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-11 px-5 text-sm font-medium text-[#FBF8F4] transition-colors duration-300"><ClipboardList className="h-4 w-4" /> Launch-day run sheet</Link>
          <div className="rounded-2xl border border-[#E8DDD3] bg-white px-5 py-4" data-testid="kit-countdown">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Launch</div>
            <div className="mt-1 font-serif text-2xl text-[#1F1A17]">{fmtDate(LAUNCH_DATE)}, 2026</div>
            <div className="text-xs text-[#685F58]">{days > 0 ? `${days} days to go` : days === 0 ? "Today" : `${-days} days ago`}</div>
          </div>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" role="tablist" data-testid="kit-tabs">
          {KIT_ORDER.map((key) => {
            const net = getNetwork(key);
            const active = key === tab;
            return (
              <button key={key} role="tab" aria-selected={active} onClick={() => setTab(key)} data-testid={`kit-tab-${key}`}
                className={`inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-medium transition-colors duration-300 ${active ? "text-white" : "border border-[#E8DDD3] bg-white text-[#1F1A17] hover:border-[#1F1A17]"}`}
                style={active ? { background: net.accent } : undefined}>
                <net.Icon className="h-4 w-4" /> {net.name}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#685F58]">
          <span className="inline-flex items-center gap-2 text-[#1F1A17]"><Icon className="h-4 w-4" style={{ color: n.accent }} /> {teasers.length} teasers · {k.posts.length} launch posts · {k.assets.length} images · {Object.keys(k.page).length} profile fields</span>
          <Link to={`/${tab}`} className="underline underline-offset-4 hover:text-[#1F1A17]" data-testid="kit-view-landing">View the {n.name} landing page <ArrowRight className="inline h-3.5 w-3.5" /></Link>
          <button type="button" onClick={downloadZip} disabled={zip.busy} data-testid="kit-download-zip" className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] disabled:opacity-60 h-11 px-5 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">
            <FolderArchive className="h-4 w-4" />
            {zip.busy ? `Packing ${zip.done}/${zip.total || "…"}` : `Download ${n.name} kit (.zip)`}
          </button>
        </div>

        <Section Icon={Type} title="Profile setup" sub="Paste each field into the page settings. Character counts shown." testId="kit-profile-section">
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(k.page).map(([label, value], i) => <Field key={label} label={label} value={value} i={i} />)}
          </div>
        </Section>

        <Section Icon={ImageIcon} title="Images" sub="Already sized for this platform. Download and upload as-is." testId="kit-assets-section">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {k.assets.map((a, i) => <Asset key={a.file + a.label} a={a} i={i} />)}
          </div>
        </Section>

        <Section Icon={Hourglass} title="Countdown teasers · 8–14 Nov" sub="One a day for the week before launch. Square for the feed, vertical for Stories." testId="kit-teasers-section">
          <div className="space-y-4">
            {teasers.map((p, i) => <Post key={p.date} p={p} i={`teaser-${i}`} accent="#1F1A17" />)}
          </div>
        </Section>

        <Section Icon={Calendar} title="Launch calendar · from 15 Nov" sub="Copy the caption (hashtags included), download the image, schedule for the date shown." testId="kit-posts-section">
          <div className="space-y-4">
            {k.posts.map((p, i) => <Post key={p.date + p.title} p={p} i={i} accent={n.accent} />)}
          </div>
        </Section>

        <ReplyBank />

        <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#685F58]">
          <Link to="/stats" className="underline underline-offset-4 hover:text-[#1F1A17]" data-testid="kit-to-stats">Open stats</Link>
          <button onClick={onLock} data-testid="kit-lock-btn" className="underline underline-offset-4 hover:text-[#1F1A17]">Lock kit</button>
        </div>
      </div>
    </FadeIn>
  );
};

export default function KitPage() {
  const { pw, setPw, lock } = useUnlocked();
  return (
    <div data-testid="kit-page" className="min-h-[60vh]">
      <Seo title="Hearthside launch kit (private)" description="Profile copy, images and launch posts for HearthVault's social pages." noindex />
      {pw ? <Kit onLock={lock} /> : (
        <PasswordGate title="Launch kit" description="Private. Enter the password to open the profile copy, images and launch calendar." onUnlock={setPw} />
      )}
    </div>
  );
}
